import { cn } from '#/shared/lib/utils'
import { RadioSelectedIcon, RadioUnselectedIcon } from '#/shared/ui/icons'

/** A labelled block of radio cards. */
export function CampaignModalSection({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <section className="flex w-full flex-col gap-2">
      <h3 className="text-2xs leading-3.5 font-medium text-warm-500">
        {label}
      </h3>
      <div role="radiogroup" aria-label={label} className="flex flex-col gap-2">
        {children}
      </div>
    </section>
  )
}

interface CampaignOptionCardProps {
  title: string
  description: string
  selected: boolean
  onSelect: () => void
  /** Sub-choices revealed under the description — chip rows, notes. */
  children?: React.ReactNode
}

/**
 * One choice in a section. The selected edge is a 1px border plus an inset ring
 * rather than a 2px border: doubling the border would re-lay the card out and
 * shift every option under it on each click.
 */
export function CampaignOptionCard({
  title,
  description,
  selected,
  onSelect,
  children,
}: CampaignOptionCardProps) {
  // The whole card is the target, not just its text — a radio option this size
  // should not have dead padding. It cannot be a <button>, because the
  // sub-choices hold buttons of their own and nesting those is invalid, so the
  // card carries the radio role and its own keyboard handling instead.
  return (
    <div
      role="radio"
      aria-checked={selected}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key !== ' ' && event.key !== 'Enter') return
        event.preventDefault()
        onSelect()
      }}
      className={cn(
        'w-full cursor-pointer rounded-lg border bg-white px-3.5 py-3 transition-colors focus-visible:outline-none focus-visible:focus-ring',
        selected
          ? 'border-campaign-running ring-1 ring-campaign-running ring-inset'
          : 'border-gray-200 hover:bg-gray-50',
      )}
    >
      <div className="flex items-start gap-3 text-left">
        {selected ? (
          <RadioSelectedIcon className="mt-0.75 size-3.5 shrink-0 text-campaign-running" />
        ) : (
          <RadioUnselectedIcon className="mt-0.75 size-3.5 shrink-0 text-gray-300" />
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <p className="text-sm leading-5 font-medium text-announce-ink">
            {title}
          </p>
          <p className="text-xs leading-4 text-warm-500">{description}</p>
        </div>
      </div>
      {children != null && selected && (
        // Chips inside a selected card must not bubble a re-select — harmless
        // today, wrong the moment sub-choices render on an unselected one.
        <div
          role="presentation"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          className="flex flex-col gap-1.5 pl-6.5"
        >
          {children}
        </div>
      )}
    </div>
  )
}

/** A `label + chips` row inside a selected option. */
export function CampaignSubChoice({
  label,
  children,
}: {
  label?: string
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 pt-1.5">
      {label != null && (
        <span className="text-xs leading-4 text-warm-500">{label}</span>
      )}
      {children}
    </div>
  )
}

export function CampaignModalChip({
  label,
  selected,
  onClick,
}: {
  label: string
  selected: boolean
  onClick?: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        'cursor-pointer rounded-md border px-2 py-1 text-xs leading-4 font-medium transition-colors',
        selected
          ? 'border-campaign-running bg-avatar-blue-soft text-campaign-running'
          : 'border-gray-200 bg-white text-warm-500 hover:bg-gray-50',
      )}
    >
      {label}
    </button>
  )
}

/**
 * The grey box that closes both modals: what the choices above would actually
 * catch. It is the only place a person can sanity-check a rolling rule before
 * committing to it.
 */
export function CampaignModalSummary({
  label,
  headline,
  notes,
}: {
  label: string
  /** The projected figure. Absent until the service can count this audience. */
  headline?: string
  notes: string[]
}) {
  return (
    <div className="flex w-full flex-col gap-1.5 rounded-lg bg-warm-25 p-3">
      <p className="text-2xs leading-3.5 font-medium text-warm-500">{label}</p>
      {headline != null && (
        <p className="text-xs leading-4.5 font-medium text-announce-ink">
          {headline}
        </p>
      )}
      {notes.map((note) => (
        <p key={note} className="text-xs leading-4.5 text-warm-500">
          {note}
        </p>
      ))}
    </div>
  )
}
